/**
 * Runs async tasks strictly one-at-a-time, in submission order.
 *
 * Each call to {@link run} returns a promise that resolves (or rejects) with
 * its OWN task's outcome, and a task that throws does not break the queue for
 * tasks submitted after it.
 *
 * Why it exists: workspace switching used to queue a "pending switch id" and
 * return early when a switch was already in flight. The caller's `await`
 * therefore resolved before the target workspace was actually established —
 * so the toolbar's hard reload (`window.location.href = '/'`) fired while the
 * new workspace id had not yet been persisted, and the reload restored the
 * PREVIOUS workspace. That is the "I had to switch twice" bug. Serializing the
 * switches makes each caller's `await` wait until ITS workspace is fully
 * active and persisted before continuing.
 */
export class SerialQueue {
  /**
   * The tail of the chain. Always resolves (never rejects) so a failed task
   * cannot poison the queue for the next one.
   */
  private tail: Promise<unknown> = Promise.resolve();

  /**
   * Enqueue `task`. It starts only after every previously-enqueued task has
   * settled. The returned promise mirrors `task`'s own resolution/rejection.
   */
  run<T>(task: () => Promise<T>): Promise<T> {
    const result = this.tail.then(() => task());
    // Swallow the result on the chain itself so a rejection here doesn't
    // reject the next task — each caller still sees its own outcome via
    // `result`.
    this.tail = result.then(
      () => undefined,
      () => undefined
    );
    return result;
  }
}
